import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Users,
  PoundSterling,
  TrendingUp,
  AlertTriangle,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Settings,
  FileSpreadsheet,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { MultiSelect } from "@/components/ui/multiselect";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";
import { formatDate, formatDateDDMMYYYY, formatCurrency, formatCreditLimit } from "@/lib/utils";
import { AllContactsTab } from "@/components/customers/all-contacts-tab";
import { AddressesTab } from "@/components/customers/addresses-tab";
import { CommunicationHistoryTab } from "@/components/customers/communication-history-tab";
import { ColumnConfigDialog } from "@/components/customers/column-config-dialog";
import { CustomerSelectionToolbar } from "@/components/customers/customer-selection-toolbar";
import { AssignStrategyDialog } from "@/components/customers/assign-strategy-dialog";
import { AssignCreditControllerDialog } from "@/components/customers/assign-credit-controller-dialog";
import { AssignAccountManagerDialog } from "@/components/customers/assign-account-manager-dialog";
import { useCustomerColumnPreferences } from "@/hooks/useCustomerColumnPreferences";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCustomersPageData } from "@/hooks/useCustomersData";
import { AgeingBarChart } from "@/components/customers/ageing-bar-chart";
import { mockStrategies } from "@/lib/mock-data/strategies-data";
import { CreateCaseDrawer } from "@/components/collections/create-case-drawer";
import { CreatePromiseDrawer } from "@/components/collections/create-promise-drawer";
import { HelpSupportButton } from "@/components/help-support";
import { mockUsers } from "@/lib/mock-users";
import { exportCustomersToExcel } from "@/lib/customer-export-utils";
import { AddNoteModal, CreateDisputeModal } from "@/components/customers/customer-action-modals";
import { useCreateDispute, useCustomerOptions, useDisputeTypeOptions, useUserOptions } from "@/hooks/useDisputesData";
import type { CreateDisputeRequest } from "@/types/api/disputes";
import { Bot, Sparkles, AlertCircle, CheckCircle2, FileText, MessageSquare } from "lucide-react";

// Helper function to render text with variable pills
const renderTextWithVariables = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  const variableRegex = /\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = variableRegex.exec(text)) !== null) {
    // Add text before the variable
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Determine badge color based on variable category
    const variable = match[0];
    const variant: 'default' | 'secondary' | 'outline' | 'accent' = 'accent';
    let accent: 'destructive' | 'success' | 'warning' | 'info' | 'teal' | 'purple' | 'nile' | 'cerulean' | 'lagoon' | 'eagle' | 'sap' | 'orange' | 'brick' | 'red' | 'violet' | 'blue-violet' = 'blue-violet';

    if (variable.includes('customer') || variable.includes('contact') || variable.includes('account')) {
      accent = 'blue-violet';
    } else if (variable.includes('transaction') || variable.includes('balance') || variable.includes('overdue')) {
      accent = 'teal';
    } else if (variable.includes('company') || variable.includes('credit_controller')) {
      accent = 'purple';
    } else if (variable.includes('payment')) {
      accent = 'orange';
    }

    // Add the variable as a badge
    parts.push(
      <Badge 
        key={key++} 
        variant={variant}
        accent={accent}
        className="text-[10px] font-mono mx-0.5 inline-flex"
      >
        {variable}
      </Badge>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

// Types
interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: "high" | "medium" | "low";
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
  collectionStrategyId: string;
  creditControllerId?: string;
  accountManagerId?: string;
}

interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "overdue" | "outstanding" | "paid";
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  allocations: {
    invoiceId: string;
    invoiceDate: string;
    amount: number;
  }[];
}

// Mock Data - 56 Customers (kept for potential future use)
const initialCustomers: Customer[] = [
  // High Risk Customers (12)
  {
    id: "1",
    name: "Acme Corporation",
    accountNumber: "ACC-001",
    currency: "GBP",
    outstanding: 214000,
    overdue: 214000,
    creditLimit: 250000,
    dso: 95,
    risk: "high",
    lastPayment: "2023-11-15",
    contact: "john.doe@acme.com",
    phone: "+1 (555) 123-4567",
    address: "123 Business St, New York, NY 10001",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-004",
  },
  {
    id: "2",
    name: "Global Tech Industries",
    accountNumber: "ACC-002",
    currency: "GBP",
    outstanding: 198000,
    overdue: 198000,
    creditLimit: 300000,
    dso: 102,
    risk: "high",
    lastPayment: "2023-10-20",
    contact: "sarah.smith@globaltech.com",
    phone: "+1 (555) 234-5678",
    address: "456 Tech Ave, San Francisco, CA 94102",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-004",
  },
  {
    id: "3",
    name: "Mega Retail Group",
    accountNumber: "ACC-003",
    currency: "EUR",
    outstanding: 187000,
    overdue: 187000,
    creditLimit: 200000,
    dso: 88,
    risk: "high",
    lastPayment: "2023-12-01",
    contact: "michael.brown@megaretail.eu",
    phone: "+44 20 7123 4567",
    address: "789 Retail Rd, London, UK",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-003",
  },
  {
    id: "4",
    name: "Premier Manufacturing",
    accountNumber: "ACC-004",
    currency: "GBP",
    outstanding: 176000,
    overdue: 176000,
    creditLimit: 220000,
    dso: 91,
    risk: "high",
    lastPayment: "2023-11-28",
    contact: "lisa.johnson@premier.com",
    phone: "+1 (555) 345-6789",
    address: "321 Factory Ln, Detroit, MI 48201",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-003",
  },
  {
    id: "5",
    name: "Sunrise Enterprises",
    accountNumber: "ACC-005",
    currency: "GBP",
    outstanding: 165000,
    overdue: 165000,
    creditLimit: 180000,
    dso: 97,
    risk: "high",
    lastPayment: "2023-11-10",
    contact: "david.wilson@sunrise.co.uk",
    phone: "+44 20 8123 4567",
    address: "654 Enterprise Way, Manchester, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-004",
  },
  {
    id: "6",
    name: "Quantum Solutions",
    accountNumber: "ACC-006",
    currency: "GBP",
    outstanding: 154000,
    overdue: 154000,
    creditLimit: 200000,
    dso: 85,
    risk: "high",
    lastPayment: "2023-12-05",
    contact: "emily.davis@quantum.com",
    phone: "+1 (555) 456-7890",
    address: "987 Innovation Dr, Austin, TX 78701",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-003",
  },
  {
    id: "7",
    name: "Atlantic Trading Co",
    accountNumber: "ACC-007",
    currency: "EUR",
    outstanding: 143000,
    overdue: 143000,
    creditLimit: 160000,
    dso: 93,
    risk: "high",
    lastPayment: "2023-11-18",
    contact: "robert.miller@atlantic.eu",
    phone: "+33 1 42 12 34 56",
    address: "234 Commerce Blvd, Paris, France",
    paymentTerms: "Net 45",
    collectionStrategyId: "",
  },
  {
    id: "8",
    name: "Pacific Imports",
    accountNumber: "ACC-008",
    currency: "AUD",
    outstanding: 132000,
    overdue: 132000,
    creditLimit: 150000,
    dso: 89,
    risk: "high",
    lastPayment: "2023-11-25",
    contact: "jennifer.taylor@pacific.au",
    phone: "+61 2 9123 4567",
    address: "567 Harbor St, Sydney, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-003",
  },
  {
    id: "9",
    name: "Northern Logistics",
    accountNumber: "ACC-009",
    currency: "CAD",
    outstanding: 121000,
    overdue: 121000,
    creditLimit: 140000,
    dso: 86,
    risk: "high",
    lastPayment: "2023-12-02",
    contact: "william.anderson@northern.ca",
    phone: "+1 (416) 123-4567",
    address: "890 Transport Ave, Toronto, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-004",
  },
  {
    id: "10",
    name: "Summit Distribution",
    accountNumber: "ACC-010",
    currency: "GBP",
    outstanding: 110000,
    overdue: 110000,
    creditLimit: 130000,
    dso: 92,
    risk: "high",
    lastPayment: "2023-11-22",
    contact: "mary.thomas@summit.com",
    phone: "+1 (555) 567-8901",
    address: "123 Distribution Way, Denver, CO 80202",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "11",
    name: "Coastal Ventures",
    accountNumber: "ACC-011",
    currency: "GBP",
    outstanding: 99000,
    overdue: 99000,
    creditLimit: 120000,
    dso: 84,
    risk: "high",
    lastPayment: "2023-12-08",
    contact: "james.jackson@coastal.co.uk",
    phone: "+44 20 9123 4567",
    address: "456 Seaside Rd, Brighton, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-003",
  },
  {
    id: "12",
    name: "Metro Services Group",
    accountNumber: "ACC-012",
    currency: "GBP",
    outstanding: 88000,
    overdue: 88000,
    creditLimit: 110000,
    dso: 87,
    risk: "high",
    lastPayment: "2023-11-30",
    contact: "patricia.white@metro.com",
    phone: "+1 (555) 678-9012",
    address: "789 Metro Plaza, Chicago, IL 60601",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-004",
  },
  // Medium Risk Customers (20)
  {
    id: "13",
    name: "Horizon Technologies",
    accountNumber: "ACC-013",
    currency: "GBP",
    outstanding: 145000,
    overdue: 72500,
    creditLimit: 200000,
    dso: 65,
    risk: "medium",
    lastPayment: "2024-01-10",
    contact: "christopher.harris@horizon.com",
    phone: "+1 (555) 789-0123",
    address: "321 Tech Park, Seattle, WA 98101",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-001",
  },
  {
    id: "14",
    name: "Silverline Industries",
    accountNumber: "ACC-014",
    currency: "EUR",
    outstanding: 138000,
    overdue: 69000,
    creditLimit: 180000,
    dso: 62,
    risk: "medium",
    lastPayment: "2024-01-15",
    contact: "barbara.martin@silverline.eu",
    phone: "+49 30 1234 5678",
    address: "654 Industrial St, Berlin, Germany",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-001",
  },
  {
    id: "15",
    name: "Apex Commercial",
    accountNumber: "ACC-015",
    currency: "GBP",
    outstanding: 127000,
    overdue: 63500,
    creditLimit: 170000,
    dso: 58,
    risk: "medium",
    lastPayment: "2024-01-20",
    contact: "daniel.thompson@apex.com",
    phone: "+1 (555) 890-1234",
    address: "987 Commerce Dr, Boston, MA 02101",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "16",
    name: "Pinnacle Systems",
    accountNumber: "ACC-016",
    currency: "GBP",
    outstanding: 116000,
    overdue: 58000,
    creditLimit: 150000,
    dso: 55,
    risk: "medium",
    lastPayment: "2024-01-25",
    contact: "nancy.garcia@pinnacle.co.uk",
    phone: "+44 20 1234 5678",
    address: "234 Systems Ave, Birmingham, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "17",
    name: "Vertex Corporation",
    accountNumber: "ACC-017",
    currency: "GBP",
    outstanding: 105000,
    overdue: 52500,
    creditLimit: 140000,
    dso: 52,
    risk: "medium",
    lastPayment: "2024-02-01",
    contact: "matthew.martinez@vertex.com",
    phone: "+1 (555) 901-2345",
    address: "567 Corporate Blvd, Atlanta, GA 30301",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-005",
  },
  {
    id: "18",
    name: "Fusion Enterprises",
    accountNumber: "ACC-018",
    currency: "CAD",
    outstanding: 94000,
    overdue: 47000,
    creditLimit: 130000,
    dso: 49,
    risk: "medium",
    lastPayment: "2024-02-05",
    contact: "karen.robinson@fusion.ca",
    phone: "+1 (604) 123-4567",
    address: "890 Enterprise Way, Vancouver, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "19",
    name: "Nexus Trading",
    accountNumber: "ACC-019",
    currency: "EUR",
    outstanding: 83000,
    overdue: 41500,
    creditLimit: 120000,
    dso: 46,
    risk: "medium",
    lastPayment: "2024-02-10",
    contact: "steven.clark@nexus.eu",
    phone: "+31 20 123 4567",
    address: "123 Trade Center, Amsterdam, Netherlands",
    paymentTerms: "Net 45",
    collectionStrategyId: "",
  },
  {
    id: "20",
    name: "Catalyst Solutions",
    accountNumber: "ACC-020",
    currency: "GBP",
    outstanding: 72000,
    overdue: 36000,
    creditLimit: 110000,
    dso: 43,
    risk: "medium",
    lastPayment: "2024-02-15",
    contact: "betty.rodriguez@catalyst.com",
    phone: "+1 (555) 012-3456",
    address: "456 Solution St, Phoenix, AZ 85001",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "21",
    name: "Zenith Group",
    accountNumber: "ACC-021",
    currency: "AUD",
    outstanding: 61000,
    overdue: 30500,
    creditLimit: 100000,
    dso: 40,
    risk: "medium",
    lastPayment: "2024-02-20",
    contact: "donald.lewis@zenith.au",
    phone: "+61 3 9123 4567",
    address: "789 Business Park, Melbourne, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "22",
    name: "Vanguard Industries",
    accountNumber: "ACC-022",
    currency: "GBP",
    outstanding: 125000,
    overdue: 62500,
    creditLimit: 160000,
    dso: 67,
    risk: "medium",
    lastPayment: "2024-01-12",
    contact: "dorothy.lee@vanguard.com",
    phone: "+1 (555) 123-4567",
    address: "321 Industrial Pkwy, Pittsburgh, PA 15201",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-001",
  },
  {
    id: "23",
    name: "Omega Systems",
    accountNumber: "ACC-023",
    currency: "GBP",
    outstanding: 114000,
    overdue: 57000,
    creditLimit: 150000,
    dso: 64,
    risk: "medium",
    lastPayment: "2024-01-18",
    contact: "joshua.walker@omega.co.uk",
    phone: "+44 20 2345 6789",
    address: "654 Tech Quarter, Leeds, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "24",
    name: "Titan Commercial",
    accountNumber: "ACC-024",
    currency: "EUR",
    outstanding: 103000,
    overdue: 51500,
    creditLimit: 140000,
    dso: 61,
    risk: "medium",
    lastPayment: "2024-01-22",
    contact: "sandra.hall@titan.eu",
    phone: "+34 91 123 4567",
    address: "987 Commercial Ave, Madrid, Spain",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-005",
  },
  {
    id: "25",
    name: "Eclipse Trading",
    accountNumber: "ACC-025",
    currency: "GBP",
    outstanding: 92000,
    overdue: 46000,
    creditLimit: 130000,
    dso: 58,
    risk: "medium",
    lastPayment: "2024-01-28",
    contact: "ashley.allen@eclipse.com",
    phone: "+1 (555) 234-5678",
    address: "234 Trade Plaza, Miami, FL 33101",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "26",
    name: "Meridian Group",
    accountNumber: "ACC-026",
    currency: "CAD",
    outstanding: 81000,
    overdue: 40500,
    creditLimit: 120000,
    dso: 55,
    risk: "medium",
    lastPayment: "2024-02-03",
    contact: "kimberly.young@meridian.ca",
    phone: "+1 (403) 123-4567",
    address: "567 Group Center, Calgary, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "27",
    name: "Spectrum Solutions",
    accountNumber: "ACC-027",
    currency: "GBP",
    outstanding: 70000,
    overdue: 35000,
    creditLimit: 110000,
    dso: 52,
    risk: "medium",
    lastPayment: "2024-02-08",
    contact: "mark.hernandez@spectrum.com",
    phone: "+1 (555) 345-6789",
    address: "890 Solution Way, Portland, OR 97201",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "28",
    name: "Prism Enterprises",
    accountNumber: "ACC-028",
    currency: "GBP",
    outstanding: 118000,
    overdue: 59000,
    creditLimit: 155000,
    dso: 69,
    risk: "medium",
    lastPayment: "2024-01-08",
    contact: "donna.king@prism.co.uk",
    phone: "+44 20 3456 7890",
    address: "123 Enterprise Rd, Glasgow, UK",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-001",
  },
  {
    id: "29",
    name: "Cascade Industries",
    accountNumber: "ACC-029",
    currency: "EUR",
    outstanding: 107000,
    overdue: 53500,
    creditLimit: 145000,
    dso: 66,
    risk: "medium",
    lastPayment: "2024-01-14",
    contact: "paul.wright@cascade.eu",
    phone: "+41 22 123 4567",
    address: "456 Industrial Zone, Geneva, Switzerland",
    paymentTerms: "Net 45",
    collectionStrategyId: "STR-001",
  },
  {
    id: "30",
    name: "Stellar Corporation",
    accountNumber: "ACC-030",
    currency: "GBP",
    outstanding: 96000,
    overdue: 48000,
    creditLimit: 135000,
    dso: 63,
    risk: "medium",
    lastPayment: "2024-01-19",
    contact: "carol.lopez@stellar.com",
    phone: "+1 (555) 456-7890",
    address: "789 Corporate Center, Dallas, TX 75201",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "31",
    name: "Quantum Dynamics",
    accountNumber: "ACC-031",
    currency: "AUD",
    outstanding: 85000,
    overdue: 42500,
    creditLimit: 125000,
    dso: 60,
    risk: "medium",
    lastPayment: "2024-01-24",
    contact: "george.hill@quantum.au",
    phone: "+61 7 9123 4567",
    address: "321 Dynamics Dr, Brisbane, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-005",
  },
  {
    id: "32",
    name: "Nexus Ventures",
    accountNumber: "ACC-032",
    currency: "GBP",
    outstanding: 74000,
    overdue: 37000,
    creditLimit: 115000,
    dso: 57,
    risk: "medium",
    lastPayment: "2024-01-30",
    contact: "ruth.scott@nexusv.com",
    phone: "+1 (555) 567-8901",
    address: "654 Venture Blvd, San Diego, CA 92101",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  // Low Risk Customers (24)
  {
    id: "33",
    name: "Bright Future Inc",
    accountNumber: "ACC-033",
    currency: "GBP",
    outstanding: 89000,
    overdue: 0,
    creditLimit: 150000,
    dso: 28,
    risk: "low",
    lastPayment: "2024-03-01",
    contact: "frank.green@brightfuture.com",
    phone: "+1 (555) 678-9012",
    address: "987 Future Ave, Minneapolis, MN 55401",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "34",
    name: "Swift Solutions",
    accountNumber: "ACC-034",
    currency: "EUR",
    outstanding: 78000,
    overdue: 0,
    creditLimit: 130000,
    dso: 25,
    risk: "low",
    lastPayment: "2024-03-05",
    contact: "virginia.adams@swift.eu",
    phone: "+32 2 123 4567",
    address: "234 Solution St, Brussels, Belgium",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-002",
  },
  {
    id: "35",
    name: "Reliable Trading Co",
    accountNumber: "ACC-035",
    currency: "GBP",
    outstanding: 67000,
    overdue: 0,
    creditLimit: 120000,
    dso: 22,
    risk: "low",
    lastPayment: "2024-03-08",
    contact: "henry.baker@reliable.co.uk",
    phone: "+44 20 4567 8901",
    address: "567 Trade Center, Edinburgh, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "36",
    name: "Premier Partners",
    accountNumber: "ACC-036",
    currency: "GBP",
    outstanding: 56000,
    overdue: 0,
    creditLimit: 100000,
    dso: 19,
    risk: "low",
    lastPayment: "2024-03-10",
    contact: "frances.gonzalez@premier.com",
    phone: "+1 (555) 789-0123",
    address: "890 Partner Plaza, Nashville, TN 37201",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-002",
  },
  {
    id: "37",
    name: "Excellence Corp",
    accountNumber: "ACC-037",
    currency: "CAD",
    outstanding: 95000,
    overdue: 4750,
    creditLimit: 140000,
    dso: 32,
    risk: "low",
    lastPayment: "2024-02-28",
    contact: "roy.nelson@excellence.ca",
    phone: "+1 (514) 123-4567",
    address: "123 Excellence Way, Montreal, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "38",
    name: "Integrity Systems",
    accountNumber: "ACC-038",
    currency: "GBP",
    outstanding: 84000,
    overdue: 4200,
    creditLimit: 130000,
    dso: 29,
    risk: "low",
    lastPayment: "2024-03-02",
    contact: "irene.carter@integrity.com",
    phone: "+1 (555) 890-1234",
    address: "456 Integrity Dr, Columbus, OH 43201",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "39",
    name: "Quality Enterprises",
    accountNumber: "ACC-039",
    currency: "EUR",
    outstanding: 73000,
    overdue: 3650,
    creditLimit: 120000,
    dso: 26,
    risk: "low",
    lastPayment: "2024-03-04",
    contact: "randy.mitchell@quality.eu",
    phone: "+43 1 123 4567",
    address: "789 Quality Blvd, Vienna, Austria",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "40",
    name: "Trustworthy Trading",
    accountNumber: "ACC-040",
    currency: "GBP",
    outstanding: 62000,
    overdue: 3100,
    creditLimit: 110000,
    dso: 23,
    risk: "low",
    lastPayment: "2024-03-06",
    contact: "marilyn.perez@trustworthy.co.uk",
    phone: "+44 20 5678 9012",
    address: "321 Trust Ave, Cardiff, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "41",
    name: "Precision Industries",
    accountNumber: "ACC-041",
    currency: "GBP",
    outstanding: 91000,
    overdue: 0,
    creditLimit: 135000,
    dso: 30,
    risk: "low",
    lastPayment: "2024-03-01",
    contact: "arthur.roberts@precision.com",
    phone: "+1 (555) 901-2345",
    address: "654 Precision Way, Indianapolis, IN 46201",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "42",
    name: "Efficient Solutions",
    accountNumber: "ACC-042",
    currency: "AUD",
    outstanding: 80000,
    overdue: 0,
    creditLimit: 125000,
    dso: 27,
    risk: "low",
    lastPayment: "2024-03-03",
    contact: "evelyn.turner@efficient.au",
    phone: "+61 8 9123 4567",
    address: "987 Efficiency Dr, Perth, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-002",
  },
  {
    id: "43",
    name: "Dependable Corp",
    accountNumber: "ACC-043",
    currency: "GBP",
    outstanding: 69000,
    overdue: 0,
    creditLimit: 115000,
    dso: 24,
    risk: "low",
    lastPayment: "2024-03-07",
    contact: "harold.phillips@dependable.com",
    phone: "+1 (555) 012-3456",
    address: "234 Dependable St, Charlotte, NC 28201",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "44",
    name: "Solid Ventures",
    accountNumber: "ACC-044",
    currency: "CAD",
    outstanding: 58000,
    overdue: 0,
    creditLimit: 105000,
    dso: 21,
    risk: "low",
    lastPayment: "2024-03-09",
    contact: "mildred.campbell@solid.ca",
    phone: "+1 (780) 123-4567",
    address: "567 Venture Park, Edmonton, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "45",
    name: "Stable Systems",
    accountNumber: "ACC-045",
    currency: "EUR",
    outstanding: 87000,
    overdue: 4350,
    creditLimit: 130000,
    dso: 31,
    risk: "low",
    lastPayment: "2024-02-29",
    contact: "clarence.parker@stable.eu",
    phone: "+45 33 12 34 56",
    address: "890 Stable Way, Copenhagen, Denmark",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "46",
    name: "Consistent Trading",
    accountNumber: "ACC-046",
    currency: "GBP",
    outstanding: 76000,
    overdue: 3800,
    creditLimit: 120000,
    dso: 28,
    risk: "low",
    lastPayment: "2024-03-02",
    contact: "carl.evans@consistent.co.uk",
    phone: "+44 20 6789 0123",
    address: "123 Consistent Rd, Liverpool, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "47",
    name: "Steady Enterprises",
    accountNumber: "ACC-047",
    currency: "GBP",
    outstanding: 65000,
    overdue: 3250,
    creditLimit: 110000,
    dso: 25,
    risk: "low",
    lastPayment: "2024-03-05",
    contact: "willie.edwards@steady.com",
    phone: "+1 (555) 123-4567",
    address: "456 Steady Ave, Milwaukee, WI 53201",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "48",
    name: "Secure Solutions",
    accountNumber: "ACC-048",
    currency: "GBP",
    outstanding: 54000,
    overdue: 0,
    creditLimit: 100000,
    dso: 18,
    risk: "low",
    lastPayment: "2024-03-11",
    contact: "lois.collins@secure.com",
    phone: "+1 (555) 234-5678",
    address: "789 Secure Blvd, Las Vegas, NV 89101",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "49",
    name: "Prompt Partners",
    accountNumber: "ACC-049",
    currency: "EUR",
    outstanding: 93000,
    overdue: 0,
    creditLimit: 135000,
    dso: 33,
    risk: "low",
    lastPayment: "2024-02-27",
    contact: "earl.stewart@prompt.eu",
    phone: "+46 8 123 4567",
    address: "321 Prompt Plaza, Stockholm, Sweden",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-002",
  },
  {
    id: "50",
    name: "Swift Industries",
    accountNumber: "ACC-050",
    currency: "AUD",
    outstanding: 82000,
    overdue: 0,
    creditLimit: 125000,
    dso: 30,
    risk: "low",
    lastPayment: "2024-03-01",
    contact: "judith.sanchez@swifti.au",
    phone: "+61 2 8123 4567",
    address: "654 Swift Way, Adelaide, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "51",
    name: "Rapid Response Co",
    accountNumber: "ACC-051",
    currency: "GBP",
    outstanding: 71000,
    overdue: 0,
    creditLimit: 115000,
    dso: 27,
    risk: "low",
    lastPayment: "2024-03-04",
    contact: "frederick.morris@rapid.com",
    phone: "+1 (555) 345-6789",
    address: "987 Response Rd, Baltimore, MD 21201",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "52",
    name: "Timely Trading",
    accountNumber: "ACC-052",
    currency: "CAD",
    outstanding: 60000,
    overdue: 0,
    creditLimit: 105000,
    dso: 24,
    risk: "low",
    lastPayment: "2024-03-07",
    contact: "joyce.rogers@timely.ca",
    phone: "+1 (204) 123-4567",
    address: "234 Timely Center, Winnipeg, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-002",
  },
  {
    id: "53",
    name: "Punctual Systems",
    accountNumber: "ACC-053",
    currency: "GBP",
    outstanding: 88000,
    overdue: 4400,
    creditLimit: 130000,
    dso: 34,
    risk: "low",
    lastPayment: "2024-02-26",
    contact: "gerald.reed@punctual.co.uk",
    phone: "+44 20 7890 1234",
    address: "567 Punctual Ave, Bristol, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "54",
    name: "Expedite Enterprises",
    accountNumber: "ACC-054",
    currency: "EUR",
    outstanding: 77000,
    overdue: 3850,
    creditLimit: 120000,
    dso: 31,
    risk: "low",
    lastPayment: "2024-02-29",
    contact: "alice.cook@expedite.eu",
    phone: "+47 22 12 34 56",
    address: "890 Expedite Way, Oslo, Norway",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-001",
  },
  {
    id: "55",
    name: "Agile Corporation",
    accountNumber: "ACC-055",
    currency: "GBP",
    outstanding: 66000,
    overdue: 3300,
    creditLimit: 110000,
    dso: 28,
    risk: "low",
    lastPayment: "2024-03-03",
    contact: "teresa.morgan@agile.com",
    phone: "+1 (555) 456-7890",
    address: "123 Agile Blvd, Sacramento, CA 95814",
    paymentTerms: "Net 30",
    collectionStrategyId: "",
  },
  {
    id: "56",
    name: "Dynamic Solutions",
    accountNumber: "ACC-056",
    currency: "GBP",
    outstanding: 55000,
    overdue: 0,
    creditLimit: 100000,
    dso: 20,
    risk: "low",
    lastPayment: "2024-03-10",
    contact: "joe.bell@dynamic.com",
    phone: "+1 (555) 567-8901",
    address: "456 Dynamic Dr, Kansas City, MO 64101",
    paymentTerms: "Net 30",
    collectionStrategyId: "STR-002",
  },
];

// Mock Invoices
const mockInvoices: Record<string, Invoice[]> = {
  "1": [
    {
      id: "INV-001",
      date: "2023-11-01",
      dueDate: "2023-12-01",
      amount: 76000,
      status: "overdue",
    },
    {
      id: "INV-002",
      date: "2023-11-15",
      dueDate: "2023-12-15",
      amount: 68000,
      status: "overdue",
    },
    {
      id: "INV-003",
      date: "2023-12-01",
      dueDate: "2023-12-31",
      amount: 70000,
      status: "overdue",
    },
  ],
};

// Mock Payments
const mockPayments: Record<string, Payment[]> = {
  "1": [
    {
      id: "PAY-001",
      date: "2023-11-15",
      amount: 50000,
      method: "Wire Transfer",
      reference: "WT-2023-001",
      allocations: [
        { invoiceId: "INV-001", invoiceDate: "2023-10-01", amount: 30000 },
        { invoiceId: "INV-002", invoiceDate: "2023-10-15", amount: 20000 },
      ],
    },
    {
      id: "PAY-002",
      date: "2023-10-20",
      amount: 35000,
      method: "ACH",
      reference: "ACH-2023-002",
      allocations: [{ invoiceId: "INV-003", invoiceDate: "2023-09-01", amount: 35000 }],
    },
  ],
};

// Collection Strategies
const collectionStrategies = [
  { id: "1", name: "Standard Collection Flow", status: "active" },
  { id: "2", name: "Intensive Collection", status: "active" },
  { id: "3", name: "Gentle Reminder Flow", status: "inactive" },
];

// Mark mock data as intentionally kept for future use
void initialCustomers;

export function CustomersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
    // Fetch customers data using mode-aware hook
    const { customers, kpis, isLoading: loading } = useCustomersPageData();
  
  // Dispute-related hooks
  const createDisputeMutation = useCreateDispute();
  const { data: customerOptions = [] } = useCustomerOptions();
  const { data: disputeTypeOptions = [] } = useDisputeTypeOptions();
  const { data: userOptions = [] } = useUserOptions();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  // Pagination state - read from URL params or use defaults
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get("page");
    return page ? parseInt(page, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get("pageSize");
    return size ? parseInt(size, 10) : 25;
  });

  // Dialog states
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [sendEmailDialog, setSendEmailDialog] = useState(false);
  const [logCallDialog, setLogCallDialog] = useState(false);
  const [addCustomerDialog, setAddCustomerDialog] = useState(false);
  const [assignStrategyDialog, setAssignStrategyDialog] = useState(false);
  const [assignCreditControllerDialog, setAssignCreditControllerDialog] = useState(false);
  const [assignAccountManagerDialog, setAssignAccountManagerDialog] = useState(false);
  const [createCaseDrawerOpen, setCreateCaseDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [createPromiseDrawerOpen, setCreatePromiseDrawerOpen] = useState(false);
  const [addNoteDialog, setAddNoteDialog] = useState(false);
  const [createDisputeDialog, setCreateDisputeDialog] = useState(false);

  // Create dispute form state
  const [createDisputeForm, setCreateDisputeForm] = useState({
    customerId: "",
    disputeTypeId: "",
    amount: "",
    statusId: "1",
    priorityId: "",
    assignedToUserId: "",
    dueDate: "",
    description: "",
  });

  // Form states
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [attachInvoiceCopies, setAttachInvoiceCopies] = useState(false);
  const [callNotes, setCallNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [editedCollectionStrategyId, setEditedCollectionStrategyId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Add customer form
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    accountNumber: "",
    contact: "",
    phone: "",
    address: "",
    currency: "GBP" as "USD" | "EUR" | "GBP" | "CAD" | "AUD",
    creditLimit: "",
    paymentTerms: "Net 30" as "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90",
    collectionStrategyId: "",
  });

  // Payment expansion state
  const [expandedPayments, setExpandedPayments] = useState<string[]>([]);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    customers: [] as string[],
    currencies: [] as string[],
    paymentTerms: [] as string[],
    collectionStrategies: [] as string[],
    creditControllers: [] as string[],
    accountManagers: [] as string[],
    outstandingMin: "",
    outstandingMax: "",
    overdueMin: "",
    overdueMax: "",
    creditLimitMin: "",
    creditLimitMax: "",
    dsoMin: "",
    dsoMax: "",
  });

  // Column configuration
  const { visibleColumns, setVisibleColumns } = useCustomerColumnPreferences();
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Sorting state
  type SortField = "accountNumber" | "name" | "contact" | "currency" | "outstanding" | 
                   "overdue" | "creditLimit" | "dso" | "risk" | "lastPayment" | 
                   "phone" | "address" | "paymentTerms" | "collectionStrategy" | 
                   "creditController" | "accountManager" |
                   "availableCredit" | "creditUtilization";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Update URL params when pagination changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("page", currentPage.toString());
    params.set("pageSize", pageSize.toString());
    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, searchParams, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, riskFilter, advancedFilters, sortField, sortDirection]);

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null (no sort)
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="h-4 w-4" />;
    }
    return <ArrowDown className="h-4 w-4" />;
  };

  // Filter and sort customers
  const filteredCustomers = useMemo(() => {
    let result = customers.filter((customer) => {
      const matchesSearch =
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.accountNumber.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRisk = riskFilter === "all" || customer.risk === riskFilter;

      // Advanced filters
      const matchesCustomer =
        advancedFilters.customers.length === 0 ||
        advancedFilters.customers.includes(customer.id);

      const matchesCurrency =
        advancedFilters.currencies.length === 0 ||
        advancedFilters.currencies.includes(customer.currency);

      const matchesPaymentTerms =
        advancedFilters.paymentTerms.length === 0 ||
        advancedFilters.paymentTerms.includes(customer.paymentTerms);

      const matchesCollectionStrategy =
        advancedFilters.collectionStrategies.length === 0 ||
        advancedFilters.collectionStrategies.includes(customer.collectionStrategyId);

      const matchesCreditController =
        advancedFilters.creditControllers.length === 0 ||
        advancedFilters.creditControllers.includes(customer.creditControllerId || "");

      const matchesAccountManager =
        advancedFilters.accountManagers.length === 0 ||
        advancedFilters.accountManagers.includes(customer.accountManagerId || "");

      const matchesOutstandingMin =
        !advancedFilters.outstandingMin ||
        customer.outstanding >= parseFloat(advancedFilters.outstandingMin);

      const matchesOutstandingMax =
        !advancedFilters.outstandingMax ||
        customer.outstanding <= parseFloat(advancedFilters.outstandingMax);

      const matchesOverdueMin =
        !advancedFilters.overdueMin ||
        customer.overdue >= parseFloat(advancedFilters.overdueMin);

      const matchesOverdueMax =
        !advancedFilters.overdueMax ||
        customer.overdue <= parseFloat(advancedFilters.overdueMax);

      const matchesCreditLimitMin =
        !advancedFilters.creditLimitMin ||
        customer.creditLimit >= parseFloat(advancedFilters.creditLimitMin);

      const matchesCreditLimitMax =
        !advancedFilters.creditLimitMax ||
        customer.creditLimit <= parseFloat(advancedFilters.creditLimitMax);

      const matchesDsoMin =
        !advancedFilters.dsoMin ||
        customer.dso >= parseFloat(advancedFilters.dsoMin);

      const matchesDsoMax =
        !advancedFilters.dsoMax ||
        customer.dso <= parseFloat(advancedFilters.dsoMax);

      return matchesSearch &&
        matchesRisk &&
        matchesCustomer &&
        matchesCurrency &&
        matchesPaymentTerms &&
        matchesCollectionStrategy &&
        matchesCreditController &&
        matchesAccountManager &&
        matchesOutstandingMin &&
        matchesOutstandingMax &&
        matchesOverdueMin &&
        matchesOverdueMax &&
        matchesCreditLimitMin &&
        matchesCreditLimitMax &&
        matchesDsoMin &&
        matchesDsoMax;
    });

    // Apply sorting if a sort field is selected
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        // Custom risk order: high > medium > low
        const riskOrder = { high: 3, medium: 2, low: 1 };

        switch (sortField) {
          case "accountNumber":
            aValue = a.accountNumber.toLowerCase();
            bValue = b.accountNumber.toLowerCase();
            break;
          case "name":
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case "contact":
            aValue = a.contact.toLowerCase();
            bValue = b.contact.toLowerCase();
            break;
          case "currency":
            aValue = a.currency;
            bValue = b.currency;
            break;
          case "outstanding":
            aValue = a.outstanding;
            bValue = b.outstanding;
            break;
          case "overdue":
            aValue = a.overdue;
            bValue = b.overdue;
            break;
          case "creditLimit":
            aValue = a.creditLimit;
            bValue = b.creditLimit;
            break;
          case "dso":
            aValue = a.dso;
            bValue = b.dso;
            break;
          case "risk":
            aValue = riskOrder[a.risk as keyof typeof riskOrder];
            bValue = riskOrder[b.risk as keyof typeof riskOrder];
            break;
          case "lastPayment":
            aValue = new Date(a.lastPayment).getTime();
            bValue = new Date(b.lastPayment).getTime();
            break;
          case "phone":
            aValue = a.phone.toLowerCase();
            bValue = b.phone.toLowerCase();
            break;
          case "address":
            aValue = a.address.toLowerCase();
            bValue = b.address.toLowerCase();
            break;
          case "paymentTerms":
            aValue = a.paymentTerms;
            bValue = b.paymentTerms;
            break;
          case "collectionStrategy":
            aValue = a.collectionStrategyId || "";
            bValue = b.collectionStrategyId || "";
            break;
          case "creditController":
            aValue = a.creditControllerId || "";
            bValue = b.creditControllerId || "";
            break;
          case "accountManager":
            aValue = a.accountManagerId || "";
            bValue = b.accountManagerId || "";
            break;
          case "availableCredit":
            aValue = a.creditLimit - a.outstanding;
            bValue = b.creditLimit - b.outstanding;
            break;
          case "creditUtilization":
            aValue = a.creditLimit > 0 ? (a.outstanding / a.creditLimit) * 100 : 0;
            bValue = b.creditLimit > 0 ? (b.outstanding / b.creditLimit) * 100 : 0;
            break;
          default:
            return 0;
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Compare values
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [customers, searchQuery, riskFilter, advancedFilters, sortField, sortDirection]);

  // Pagination calculations
  const totalRecords = filteredCustomers.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  
  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Get paginated customers
  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCustomers.slice(startIndex, endIndex);
  }, [filteredCustomers, currentPage, pageSize]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near the start
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Get selected customer for floating menu
  const selectedCustomer_floatingMenu =
    selectedCustomers.length === 1
      ? customers.find((c) => c.id === selectedCustomers[0])
      : null;

  // Risk badge helper
  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case "high":
        return <Badge variant="destructive">High Risk</Badge>;
      case "medium":
        return (
          <Badge className="bg-amber-500 text-white hover:bg-amber-600">Medium Risk</Badge>
        );
      case "low":
        return <Badge className="bg-green-600 text-white hover:bg-green-700">Low Risk</Badge>;
    }
  };

  // Column rendering helpers
  const getColumnHeader = (columnId: string) => {
    const headers: Record<string, { label: string; className?: string }> = {
      accountNumber: { label: "Account #" },
      name: { label: "Name" },
      contact: { label: "Contact Email" },
      currency: { label: "Currency" },
      outstanding: { label: "Outstanding", className: "text-right" },
      overdue: { label: "Overdue", className: "text-right" },
      creditLimit: { label: "Credit Limit", className: "text-right" },
      dso: { label: "Performance" },
      risk: { label: "Risk" },
      lastPayment: { label: "Last Payment" },
      phone: { label: "Phone" },
      address: { label: "Address" },
      paymentTerms: { label: "Payment Terms" },
      collectionStrategy: { label: "Collection Strategy" },
      creditController: { label: "Credit Controller" },
      accountManager: { label: "Account Manager" },
      availableCredit: { label: "Available Credit", className: "text-right" },
      creditUtilization: { label: "Credit Utilization %", className: "text-right" },
    };
    return headers[columnId] || { label: columnId };
  };

  const renderColumnCell = (columnId: string, customer: Customer) => {
    switch (columnId) {
      case "accountNumber":
        return <TableCell className="font-medium">{customer.accountNumber}</TableCell>;

      case "name":
        return (
          <TableCell>
            <button
              onClick={() => handleViewDetails(customer)}
              className="text-primary hover:underline"
            >
              {customer.name}
            </button>
          </TableCell>
        );

      case "contact":
        return <TableCell>{customer.contact}</TableCell>;

      case "currency":
        return <TableCell>{customer.currency}</TableCell>;

      case "outstanding":
        return (
          <TableCell className="text-right">
            {formatCurrency(customer.outstanding, customer.currency)}
          </TableCell>
        );

      case "overdue":
        return (
          <TableCell className={`text-right ${customer.overdue > 0 ? "text-red-600" : ""}`}>
            {formatCurrency(customer.overdue, customer.currency)}
          </TableCell>
        );

      case "creditLimit":
        return (
          <TableCell className="text-right">
            {formatCreditLimit(customer.creditLimit, customer.currency)}
          </TableCell>
        );

      case "dso":
        return (
          <TableCell>
            <Badge variant={customer.dso > 60 ? "destructive" : "secondary"}>
              {customer.dso} days
            </Badge>
          </TableCell>
        );

      case "risk":
        return <TableCell>{getRiskBadge(customer.risk)}</TableCell>;

      case "lastPayment":
        return <TableCell>{formatDateDDMMYYYY(customer.lastPayment)}</TableCell>;

      case "phone":
        return <TableCell>{customer.phone}</TableCell>;

      case "address":
        return <TableCell className="max-w-[200px] truncate">{customer.address}</TableCell>;

      case "paymentTerms":
        return <TableCell>{customer.paymentTerms}</TableCell>;

      case "collectionStrategy": {
        const strategy = mockStrategies.find(s => s.id === customer.collectionStrategyId);
        return (
          <TableCell>
            {strategy ? (
              <Badge variant="outline">{strategy.name}</Badge>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );
      }

      case "creditController": {
        const user = mockUsers.find(u => u.id === customer.creditControllerId);
        return (
          <TableCell>
            {user ? (
              <span>{user.firstName} {user.lastName}</span>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );
      }

      case "accountManager": {
        const user = mockUsers.find(u => u.id === customer.accountManagerId);
        return (
          <TableCell>
            {user ? (
              <span>{user.firstName} {user.lastName}</span>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );
      }

            case "availableCredit": {
              const availableCredit = customer.creditLimit - customer.outstanding;
              return (
                <TableCell className="text-right">
                  {formatCreditLimit(availableCredit, customer.currency)}
                </TableCell>
              );
            }

            case "creditUtilization": {
              const utilization = customer.creditLimit > 0
                ? ((customer.outstanding / customer.creditLimit) * 100).toFixed(1)
                : "0.0";
              return (
                <TableCell className="text-right">
                  <Badge
                    variant={parseFloat(utilization) > 80 ? "destructive" : parseFloat(utilization) > 60 ? "secondary" : "default"}
                  >
                    {utilization}%
                  </Badge>
                </TableCell>
              );
            }

      default:
        return <TableCell>-</TableCell>;
    }
  };

  // Handle checkbox selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all customers on current page
      setSelectedCustomers(paginatedCustomers.map((c) => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId: string, checked: boolean) => {
    if (checked) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    } else {
      setSelectedCustomers(selectedCustomers.filter((id) => id !== customerId));
    }
  };

  // Check if all customers on current page are selected
  const allCurrentPageSelected = paginatedCustomers.length > 0 && 
    paginatedCustomers.every(customer => selectedCustomers.includes(customer.id));

  // Handle view details
  const handleViewDetails = (customer: Customer) => {
    navigate(`/customers/${customer.id}`, { state: { customer } });
  };

  // Handle send email
  const handleSendEmail = () => {
    if (selectedCustomers.length === 0) return;

    // Use variable placeholders instead of actual data
    const subject = `Payment Reminder - {{account_number}}`;
    const body = `Dear {{customer_name}},

This is a friendly reminder regarding your outstanding balance.

Outstanding Amount: {{account_balance}}
Overdue Amount: {{overdue_balance}}

Please contact us if you have any questions.

Best regards,
{{credit_controller}}`;

    setEmailSubject(subject);
    setEmailBody(body);
    setSendEmailDialog(true);
  };

  // Handle log call
  const handleLogCall = () => {
    setLogCallDialog(true);
  };

  // Handle assign collection strategy
  const handleAssignStrategy = () => {
    if (selectedCustomers.length === 0) return;
    setAssignStrategyDialog(true);
  };

  // Handle create collection case
  const handleCreateCollectionCase = () => {
    if (selectedCustomers.length !== 1) return;
    setCreateCaseDrawerOpen(true);
  };

  // Handle create promise to pay
  const handleCreatePromise = () => {
    if (selectedCustomers.length !== 1) return;
    setCreatePromiseDrawerOpen(true);
  };

  // Handle add note
  const handleAddNote = () => {
    if (selectedCustomers.length !== 1) return;
    setAddNoteDialog(true);
  };

  // Handle create dispute
  const handleCreateDispute = () => {
    if (selectedCustomers.length !== 1) return;
    
    // Pre-populate the customer in the form
    const customer = customers.find(c => c.id === selectedCustomers[0]);
    if (customer) {
      setCreateDisputeForm(prev => ({
        ...prev,
        customerId: customer.id,
      }));
    }
    
    setCreateDisputeDialog(true);
  };

  // Handle dispute form changes
  const handleDisputeFormChange = (field: string, value: string) => {
    setCreateDisputeForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle create dispute submission
  const handleSubmitCreateDispute = async () => {
    if (!createDisputeForm.customerId || !createDisputeForm.disputeTypeId || !createDisputeForm.amount || !createDisputeForm.priorityId) {
      return;
    }

    const request: CreateDisputeRequest = {
      customerId: parseInt(createDisputeForm.customerId),
      disputeTypeId: parseInt(createDisputeForm.disputeTypeId),
      amount: parseFloat(createDisputeForm.amount),
      statusId: parseInt(createDisputeForm.statusId),
      priorityId: parseInt(createDisputeForm.priorityId),
      assignedToUserId: createDisputeForm.assignedToUserId ? parseInt(createDisputeForm.assignedToUserId) : undefined,
      dueDate: createDisputeForm.dueDate || undefined,
      description: createDisputeForm.description || undefined,
    };

    try {
      await createDisputeMutation.mutateAsync(request);
      setCreateDisputeDialog(false);
      setCreateDisputeForm({
        customerId: "",
        disputeTypeId: "",
        amount: "",
        statusId: "1",
        priorityId: "",
        assignedToUserId: "",
        dueDate: "",
        description: "",
      });
      setSelectedCustomers([]);
      toast.success("Dispute created successfully");
    } catch (error) {
      console.error("Failed to create dispute:", error);
      toast.error("Failed to create dispute");
    }
  };

  // Handle strategy assignment confirmation
  const handleConfirmStrategyAssignment = (strategyId: string) => {
    if (selectedCustomers.length === 0) return;

    // Get all selected customer objects
    const selectedCustomerObjects = customers.filter((c) => selectedCustomers.includes(c.id));
    
    // In a real app, this would make an API call to update the strategies
    // For now, we'll just show a success toast
    const newAssignments = selectedCustomerObjects.filter(c => !c.collectionStrategyId || c.collectionStrategyId === "").length;
    const updates = selectedCustomerObjects.length - newAssignments;
    
    const strategy = mockStrategies.find(s => s.id === strategyId);
    const strategyName = strategy?.name || "Unknown Strategy";

    toast.success("Collection Strategy Assigned", {
      description: `Successfully assigned "${strategyName}" to ${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? "s" : ""} (${newAssignments} new, ${updates} updated)`,
    });

    // Clear selection after assignment
    setSelectedCustomers([]);
  };

  // Handle assign credit controller
  const handleAssignCreditController = () => {
    if (selectedCustomers.length === 0) return;
    setAssignCreditControllerDialog(true);
  };

  // Handle credit controller assignment confirmation
  const handleConfirmCreditControllerAssignment = (userId: string) => {
    if (selectedCustomers.length === 0) return;

    // Get all selected customer objects
    const selectedCustomerObjects = customers.filter((c) => selectedCustomers.includes(c.id));
    
    // In a real app, this would make an API call to update the credit controllers
    // For now, we'll just show a success toast
    const newAssignments = selectedCustomerObjects.filter(c => !c.creditControllerId || c.creditControllerId === "").length;
    const updates = selectedCustomerObjects.length - newAssignments;
    
    const user = mockUsers.find(u => u.id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : "Unknown User";

    toast.success("Credit Controller Assigned", {
      description: `Successfully assigned "${userName}" to ${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? "s" : ""} (${newAssignments} new, ${updates} updated)`,
    });

    // Clear selection after assignment
    setSelectedCustomers([]);
  };

  // Handle assign account manager
  const handleAssignAccountManager = () => {
    if (selectedCustomers.length === 0) return;
    setAssignAccountManagerDialog(true);
  };

  // Handle account manager assignment confirmation
  const handleConfirmAccountManagerAssignment = (userId: string) => {
    if (selectedCustomers.length === 0) return;

    // Get all selected customer objects
    const selectedCustomerObjects = customers.filter((c) => selectedCustomers.includes(c.id));
    
    // In a real app, this would make an API call to update the account managers
    // For now, we'll just show a success toast
    const newAssignments = selectedCustomerObjects.filter(c => !c.accountManagerId || c.accountManagerId === "").length;
    const updates = selectedCustomerObjects.length - newAssignments;
    
    const user = mockUsers.find(u => u.id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : "Unknown User";

    toast.success("Account Manager Assigned", {
      description: `Successfully assigned "${userName}" to ${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? "s" : ""} (${newAssignments} new, ${updates} updated)`,
    });

    // Clear selection after assignment
    setSelectedCustomers([]);
  };

  // Handle export statement
  const handleExportStatement = () => {
    if (selectedCustomers.length === 0) return;

    // Get all selected customer objects
    const selectedCustomerObjects = customers.filter((c) => selectedCustomers.includes(c.id));

    if (selectedCustomerObjects.length === 0) return;

    // Build CSV with header and all selected customers
    let csv = `Account Number,Name,Outstanding,Overdue,Credit Limit,Performance,Risk\n`;

    selectedCustomerObjects.forEach((customer) => {
      csv += `${customer.accountNumber},${customer.name},${customer.outstanding},${customer.overdue},${Math.round(customer.creditLimit)},${customer.dso},${customer.risk}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    // Use different filename for single vs multiple customers
    if (selectedCustomerObjects.length === 1) {
      a.download = `statement-${selectedCustomerObjects[0].accountNumber}-${new Date().toISOString().split("T")[0]}.csv`;
    } else {
      a.download = `statements-bulk-${new Date().toISOString().split("T")[0]}.csv`;
    }

    a.click();

    const message = selectedCustomerObjects.length === 1
      ? "Statement exported successfully"
      : `${selectedCustomerObjects.length} statements exported successfully`;
    toast.success(message);
  };

  // Handle export to Excel
  const handleExportToExcel = () => {
    try {
      exportCustomersToExcel(
        filteredCustomers,
        `customers-export-${new Date().toISOString().split("T")[0]}.csv`,
        visibleColumns
      );
      toast.success(`Successfully exported ${filteredCustomers.length} customer records`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  // Handle export all columns to Excel
  const handleExportAllColumns = () => {
    try {
      exportCustomersToExcel(
        filteredCustomers,
        `customers-all-columns-export-${new Date().toISOString().split("T")[0]}.csv`
        // No visibleColumns parameter = export all columns
      );
      toast.success(`Successfully exported ${filteredCustomers.length} customer records with all columns`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  // Handle save collection strategy
  const handleSaveCollectionStrategy = () => {
    if (!selectedCustomer) return;

    // Note: In a real implementation, this would call an API to update the customer
    // For now, just update the selected customer in the dialog
    setSelectedCustomer({ ...selectedCustomer, collectionStrategyId: editedCollectionStrategyId });

    toast.success("Collection strategy updated successfully");
  };

  // Handle add customer
  const handleAddCustomer = () => {
    // Validation
    if (!newCustomer.name || !newCustomer.accountNumber || !newCustomer.contact || !newCustomer.phone || !newCustomer.address || !newCustomer.creditLimit) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCustomer.contact)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Account number uniqueness
    if (customers.some((c) => c.accountNumber === newCustomer.accountNumber)) {
      toast.error("Account number already exists");
      return;
    }

    // Credit limit validation
    const creditLimit = parseFloat(newCustomer.creditLimit);
    if (isNaN(creditLimit) || creditLimit <= 0) {
      toast.error("Credit limit must be a positive number");
      return;
    }

    // Note: In a real implementation, this would call an API to create the customer
    // For now, just close the dialog and show success message
    setAddCustomerDialog(false);
    setNewCustomer({
      name: "",
      accountNumber: "",
      contact: "",
      phone: "",
      address: "",
      currency: "GBP",
      creditLimit: "",
      paymentTerms: "Net 30",
      collectionStrategyId: "",
    });

    toast.success("Customer added successfully");
  };

  // Handle send email submit
  const handleSendEmailSubmit = () => {
    const count = selectedCustomers.length;
    toast.success(`Email sent successfully to ${count} customer${count !== 1 ? 's' : ''}`);
    setSendEmailDialog(false);
    setEmailSubject("");
    setEmailBody("");
    setAttachInvoiceCopies(false);
    setSelectedCustomers([]);
  };

  // Handle log call submit
  const handleLogCallSubmit = () => {
    if (!callNotes || !callOutcome) {
      toast.error("Please fill in all fields");
      return;
    }

    toast.success("Call logged successfully");
    setLogCallDialog(false);
    setCallNotes("");
    setCallOutcome("");
  };

  // Calculate aging
  const calculateAging = (customer: Customer) => {
    const invoices = mockInvoices[customer.id] || [];
    const today = new Date();

    const aging = {
      current: 0,
      days1_30: 0,
      days31_60: 0,
      days61_90: 0,
      days90Plus: 0,
    };

    invoices.forEach((invoice) => {
      if (invoice.status === "paid") return;

      const dueDate = new Date(invoice.dueDate);
      const daysOverdue = Math.floor(
        (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysOverdue < 0) aging.current += invoice.amount;
      else if (daysOverdue <= 30) aging.days1_30 += invoice.amount;
      else if (daysOverdue <= 60) aging.days31_60 += invoice.amount;
      else if (daysOverdue <= 90) aging.days61_90 += invoice.amount;
      else aging.days90Plus += invoice.amount;
    });

    return aging;
  };

  // Toggle payment expansion
  const togglePaymentExpansion = (paymentId: string) => {
    if (expandedPayments.includes(paymentId)) {
      setExpandedPayments(expandedPayments.filter((id) => id !== paymentId));
    } else {
      setExpandedPayments([...expandedPayments, paymentId]);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Customer Accounts</h1>
        </div>
        <HelpSupportButton screenId="customers" />
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-all hover:shadow-md border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-green-100 flex-shrink-0">
                    <Users className="h-4 w-4 text-green-600" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Total Customers
                  </p>
                </div>
                <p className="text-2xl font-bold">
                  {kpis.totalCustomers}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Active customer accounts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-orange-100 flex-shrink-0">
                    <PoundSterling className="h-4 w-4 text-orange-600" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Outstanding Balance
                  </p>
                </div>
                <p className="text-2xl font-bold">
                  {formatCurrency(kpis.totalOutstanding, "GBP")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Total across all customers
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                    <TrendingUp className="h-4 w-4 text-red-600" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Overdue Balance
                  </p>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(kpis.totalOverdue, "GBP")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Requires immediate attention
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    High Risk Customers
                  </p>
                </div>
                <p className="text-2xl font-bold">
                  {kpis.highRiskCount}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Customers requiring monitoring
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Customers</CardTitle>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      className="h-8 w-8"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
              <div className="mb-4 flex flex-col gap-4 md:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or account number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Filter by risk" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risk Levels</SelectItem>
                    <SelectItem value="high">High Risk</SelectItem>
                    <SelectItem value="medium">Medium Risk</SelectItem>
                    <SelectItem value="low">Low Risk</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => setMoreFiltersOpen(true)}>
                  <Filter className="mr-2 h-4 w-4" />
                  More Filters
                </Button>
              </div>

              {/* Table */}
              <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={allCurrentPageSelected}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.map((columnId) => {
                    const header = getColumnHeader(columnId);
                    const isSortable = [
                      "accountNumber", "name", "contact", "currency", "outstanding",
                      "overdue", "creditLimit", "dso", "risk", "lastPayment",
                      "phone", "address", "paymentTerms", "collectionStrategy",
                      "availableCredit", "creditUtilization"
                    ].includes(columnId);
                    
                    return (
                      <TableHead key={columnId} className={header.className}>
                        {isSortable ? (
                          <button
                            onClick={() => handleSort(columnId as SortField)}
                            className="flex items-center gap-1 hover:text-foreground transition-colors font-medium -ml-3 pl-3 pr-2 py-1 rounded hover:bg-muted/50"
                          >
                            <span>{header.label}</span>
                            <span className={sortField === columnId ? "text-foreground" : "text-muted-foreground/40"}>
                              {getSortIcon(columnId as SortField)}
                            </span>
                          </button>
                        ) : (
                          header.label
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading customers...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-64 text-center">
                      <p className="text-sm text-muted-foreground">No customers found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCustomers.includes(customer.id)}
                          onCheckedChange={(checked) =>
                            handleSelectCustomer(customer.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      {visibleColumns.map((columnId) => (
                        <React.Fragment key={columnId}>
                          {renderColumnCell(columnId, customer)}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {!loading && filteredCustomers.length > 0 && (
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Pagination Info and Page Size Selector */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {Math.min((currentPage - 1) * pageSize + 1, totalRecords)} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} customers
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="page-size" className="text-sm text-muted-foreground whitespace-nowrap">
                    Rows per page:
                  </Label>
                  <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                    <SelectTrigger id="page-size" className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Pagination Buttons */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => handlePageChange(currentPage - 1)}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {getPageNumbers().map((page, index) => (
                      <PaginationItem key={index}>
                        {page === 'ellipsis' ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink
                            onClick={() => handlePageChange(page as number)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => handlePageChange(currentPage + 1)}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          )}
        </CardContent>
      </Card>



      {/* Customer Details Dialog */}
      <Dialog open={viewDetailsDialog} onOpenChange={setViewDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer?.name} - {selectedCustomer?.accountNumber}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-120px)]">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="financial">Financial</TabsTrigger>
                <TabsTrigger value="ageing">Ageing</TabsTrigger>
                <TabsTrigger value="invoices">Invoices</TabsTrigger>
                <TabsTrigger value="payments">Payments</TabsTrigger>
                <TabsTrigger value="communications">Communications</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
                <TabsTrigger value="addresses">Addresses</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Account Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Account Number</Label>
                        <p className="font-medium">{selectedCustomer?.accountNumber}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Customer Name</Label>
                        <p className="font-medium">{selectedCustomer?.name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{selectedCustomer && getRiskBadge(selectedCustomer.risk)}</div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium">{selectedCustomer?.currency}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Email</Label>
                        <p className="font-medium">{selectedCustomer?.contact}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Phone</Label>
                        <p className="font-medium">{selectedCustomer?.phone}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Address</Label>
                        <p className="font-medium">{selectedCustomer?.address}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Collection Strategy</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <Label>Assigned Strategy</Label>
                        <Select
                          value={editedCollectionStrategyId}
                          onValueChange={setEditedCollectionStrategyId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select strategy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {collectionStrategies
                              .filter((s) => s.status === "active")
                              .map((strategy) => (
                                <SelectItem key={strategy.id} value={strategy.id}>
                                  {strategy.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleSaveCollectionStrategy}
                        disabled={
                          editedCollectionStrategyId === selectedCustomer?.collectionStrategyId
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Quick Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div>
                        <Label className="text-muted-foreground">Outstanding</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer &&
                            formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Overdue</Label>
                        <p className="text-2xl font-bold text-red-600">
                          {selectedCustomer &&
                            formatCurrency(selectedCustomer.overdue, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Performance</Label>
                        <p className="text-2xl font-bold">{selectedCustomer?.dso} days</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Last Payment</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatDate(selectedCustomer.lastPayment)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Financial Tab */}
              <TabsContent value="financial" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Outstanding Balance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Overdue Amount</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.overdue, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Credit Limit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Available Credit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">
                        {selectedCustomer &&
                          formatCurrency(
                            selectedCustomer.creditLimit - selectedCustomer.outstanding,
                            selectedCustomer.currency
                          )}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Terms & Performance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <Label className="text-muted-foreground">Payment Terms</Label>
                        <p className="text-lg font-medium">{selectedCustomer?.paymentTerms}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Days Sales Outstanding</Label>
                        <p className="text-lg font-medium">{selectedCustomer?.dso} days</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{selectedCustomer && getRiskBadge(selectedCustomer.risk)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Credit Utilization</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}{" "}
                        of{" "}
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                      </span>
                      <span className="font-medium">
                        {selectedCustomer &&
                          Math.round(
                            (selectedCustomer.outstanding / selectedCustomer.creditLimit) * 100
                          )}
                        %
                      </span>
                    </div>
                    <Progress
                      value={
                        selectedCustomer
                          ? (selectedCustomer.outstanding / selectedCustomer.creditLimit) * 100
                          : 0
                      }
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Ageing Tab */}
              <TabsContent value="ageing" className="space-y-4">
                {selectedCustomer && (() => {
                                                                        const aging = calculateAging(selectedCustomer);
                                                                        const overdue = aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;

                  return (
                    <>
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0">
                          <CardTitle>Ageing Breakdown</CardTitle>
                          {overdue > 0 && (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Close dialog and navigate to customer detail page
                                  setViewDetailsDialog(false);
                                  navigate(`/customer/${selectedCustomer.id}`);
                                  toast.info("Opening customer details to send reminders");
                                }}
                                className="h-8"
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                Send Reminders
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Close dialog and navigate to customer detail page collections tab
                                  setViewDetailsDialog(false);
                                  navigate(`/customer/${selectedCustomer.id}?tab=collections`);
                                  toast.info("Opening Collections tab to escalate account");
                                }}
                                className="h-8"
                              >
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Escalate to Collections
                              </Button>
                            </div>
                          )}
                        </CardHeader>
                        <CardContent>
                          <AgeingBarChart 
                            data={aging}
                            onSendReminders={() => {
                              // Close dialog and navigate to customer detail page
                              setViewDetailsDialog(false);
                              navigate(`/customer/${selectedCustomer.id}`);
                              toast.info("Opening customer details to send reminders");
                            }}
                            onEscalateToCollections={() => {
                              // Close dialog and navigate to customer detail page collections tab
                              setViewDetailsDialog(false);
                              navigate(`/customer/${selectedCustomer.id}?tab=collections`);
                              toast.info("Opening Collections tab to escalate account");
                            }}
                          />
                        </CardContent>
                      </Card>

                      <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Current & Not Overdue</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-3xl font-bold text-green-600">
                              {formatCurrency(aging.current, selectedCustomer.currency)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Overdue (30+ Days)</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-3xl font-bold text-red-600">
                              {formatCurrency(overdue, selectedCustomer.currency)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="invoices" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Unpaid Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCustomer &&
                          (mockInvoices[selectedCustomer.id] || []).map((invoice) => {
                            const today = new Date();
                            const dueDate = new Date(invoice.dueDate);
                            const daysOverdue = Math.floor(
                              (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
                            );

                            return (
                              <TableRow key={invoice.id}>
                                <TableCell className="font-medium">{invoice.id}</TableCell>
                                <TableCell>{formatDate(invoice.date)}</TableCell>
                                <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(invoice.amount, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      invoice.status === "overdue" ? "destructive" : "secondary"
                                    }
                                  >
                                    {invoice.status}
                                  </Badge>
                                </TableCell>
                                <TableCell
                                  className={`text-right ${daysOverdue > 0 ? "text-red-600" : ""}`}
                                >
                                  {daysOverdue > 0 ? daysOverdue : 0}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Payment History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]"></TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCustomer &&
                          (mockPayments[selectedCustomer.id] || []).map((payment) => (
                            <>
                              <TableRow key={payment.id}>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => togglePaymentExpansion(payment.id)}
                                  >
                                    {expandedPayments.includes(payment.id) ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell className="font-medium">{payment.id}</TableCell>
                                <TableCell>{formatDate(payment.date)}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(payment.amount, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell>{payment.method}</TableCell>
                                <TableCell>{payment.reference}</TableCell>
                              </TableRow>
                              {expandedPayments.includes(payment.id) && (
                                <TableRow>
                                  <TableCell colSpan={6} className="bg-muted/50">
                                    <div className="p-4">
                                      <p className="mb-2 font-semibold">Invoice Allocations:</p>
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Invoice #</TableHead>
                                            <TableHead>Invoice Date</TableHead>
                                            <TableHead className="text-right">
                                              Allocated Amount
                                            </TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {payment.allocations.map((allocation, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell>{allocation.invoiceId}</TableCell>
                                              <TableCell>
                                                {formatDate(allocation.invoiceDate)}
                                              </TableCell>
                                              <TableCell className="text-right">
                                                {formatCurrency(
                                                  allocation.amount,
                                                  selectedCustomer.currency
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-muted-foreground">Total Payments (Last 3 Months)</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer &&
                            formatCurrency(
                              (mockPayments[selectedCustomer.id] || []).reduce(
                                (sum, p) => sum + p.amount,
                                0
                              ),
                              selectedCustomer.currency
                            )}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Last Payment Date</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatDate(selectedCustomer.lastPayment)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Communications Tab */}
              <TabsContent value="communications">
                {selectedCustomer && (
                  <CommunicationHistoryTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                  />
                )}
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts">
                {selectedCustomer && (
                  <AllContactsTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                  />
                )}
              </TabsContent>

              {/* Addresses Tab */}
              <TabsContent value="addresses">
                {selectedCustomer && (
                  <AddressesTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                    primaryAddress={selectedCustomer.address}
                  />
                )}
              </TabsContent>
            </Tabs>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={sendEmailDialog} onOpenChange={setSendEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              Send an email to {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/50">
                <Badge variant="accent" accent="blue-violet" className="text-[10px] font-mono">
                  {'{{customer_email}}'}
                </Badge>
              </div>
            </div>
            <div>
              <Label>Subject</Label>
              <div className="px-3 py-2 border rounded-md bg-background min-h-[40px] flex items-center flex-wrap gap-1">
                {renderTextWithVariables(emailSubject)}
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <div className="px-3 py-2 border rounded-md bg-background min-h-[200px] whitespace-pre-wrap">
                {emailBody.split('\n').map((line, i) => (
                  <div key={i} className="flex items-start flex-wrap gap-1">
                    {renderTextWithVariables(line)}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="attach-invoices" 
                checked={attachInvoiceCopies}
                onCheckedChange={(checked) => setAttachInvoiceCopies(checked as boolean)}
              />
              <Label 
                htmlFor="attach-invoices" 
                className="text-sm font-normal cursor-pointer"
              >
                Attach copy invoice(s)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmailSubmit}>
              Send Email to {selectedCustomers.length} Customer{selectedCustomers.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Call Dialog */}
      <Dialog open={logCallDialog} onOpenChange={setLogCallDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>Record details of your call with the customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer</Label>
              <Input value={selectedCustomer_floatingMenu?.name || ""} disabled />
            </div>
            <div>
              <Label>Contact</Label>
              <Input value={selectedCustomer_floatingMenu?.contact || ""} disabled />
            </div>
            <div>
              <Label>Call Outcome</Label>
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment_promised">Payment Promised</SelectItem>
                  <SelectItem value="dispute_raised">Dispute Raised</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="left_voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="payment_plan_agreed">Payment Plan Agreed</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Call Notes *</Label>
              <Textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={5}
                placeholder="Enter call notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogCallDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogCallSubmit}>Save Call Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerDialog} onOpenChange={setAddCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
            <DialogDescription>Create a new customer account</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={newCustomer.accountNumber}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, accountNumber: e.target.value })
                  }
                  placeholder="Enter account number"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={newCustomer.contact}
                  onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label>Address *</Label>
                <Textarea
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  placeholder="Enter address"
                  rows={3}
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={newCustomer.currency}
                  onValueChange={(value: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD') => setNewCustomer({ ...newCustomer, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credit Limit *</Label>
                <Input
                  type="number"
                  value={newCustomer.creditLimit}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, creditLimit: e.target.value })
                  }
                  placeholder="Enter credit limit"
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={newCustomer.paymentTerms}
                  onValueChange={(value: 'Net 15' | 'Net 30' | 'Net 45' | 'Net 60' | 'Net 90') =>
                    setNewCustomer({ ...newCustomer, paymentTerms: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                    <SelectItem value="Net 90">Net 90</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Collection Strategy (Optional)</Label>
                <Select
                  value={newCustomer.collectionStrategyId}
                  onValueChange={(value) =>
                    setNewCustomer({ ...newCustomer, collectionStrategyId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {collectionStrategies
                      .filter((s) => s.status === "active")
                      .map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          {strategy.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCustomerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCustomer}>Add Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* More Filters Sheet */}
      <Sheet open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>More Filters</SheetTitle>
            <SheetDescription>
              Filter customers by multiple criteria
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-180px)] pr-4 pl-4 mt-6">
            <div className="space-y-4">
              {/* Customer Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Customer</Label>
                <MultiSelect
                  options={customers.map((customer) => ({
                    value: customer.id,
                    label: customer.name,
                  }))}
                  placeholder="Select customers..."
                  emptyMessage="No customers found."
                  value={advancedFilters.customers}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      customers: values,
                    })
                  }
                />
              </div>

              {/* Currency Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Currency</Label>
                <MultiSelect
                  options={["USD", "EUR", "GBP", "CAD", "AUD"].map((currency) => ({
                    value: currency,
                    label: currency,
                  }))}
                  placeholder="Select currencies..."
                  emptyMessage="No currencies found."
                  value={advancedFilters.currencies}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      currencies: values,
                    })
                  }
                />
              </div>

              {/* Payment Terms Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Payment Terms</Label>
                <MultiSelect
                  options={["Net 15", "Net 30", "Net 45", "Net 60", "Net 90"].map((term) => ({
                    value: term,
                    label: term,
                  }))}
                  placeholder="Select payment terms..."
                  emptyMessage="No payment terms found."
                  value={advancedFilters.paymentTerms}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      paymentTerms: values,
                    })
                  }
                />
              </div>

              {/* Collection Strategy Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Collection Strategy</Label>
                <MultiSelect
                  options={[
                    { value: "", label: "None" },
                    ...collectionStrategies
                      .filter((s) => s.status === "active")
                      .map((strategy) => ({
                        value: strategy.id,
                        label: strategy.name,
                      }))
                  ]}
                  placeholder="Select collection strategies..."
                  emptyMessage="No strategies found."
                  value={advancedFilters.collectionStrategies}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      collectionStrategies: values,
                    })
                  }
                />
              </div>

              {/* Credit Controller Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Credit Controller</Label>
                <MultiSelect
                  options={[
                    { value: "", label: "None" },
                    ...mockUsers.map((user) => ({
                      value: user.id,
                      label: `${user.firstName} ${user.lastName}`,
                    }))
                  ]}
                  placeholder="Select credit controllers..."
                  emptyMessage="No users found."
                  value={advancedFilters.creditControllers}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      creditControllers: values,
                    })
                  }
                />
              </div>

              {/* Account Manager Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Account Manager</Label>
                <MultiSelect
                  options={[
                    { value: "", label: "None" },
                    ...mockUsers.map((user) => ({
                      value: user.id,
                      label: `${user.firstName} ${user.lastName}`,
                    }))
                  ]}
                  placeholder="Select account managers..."
                  emptyMessage="No users found."
                  value={advancedFilters.accountManagers}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      accountManagers: values,
                    })
                  }
                />
              </div>

              <Separator />

              {/* Outstanding Balance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Outstanding Balance</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.outstandingMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          outstandingMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.outstandingMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          outstandingMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Overdue Balance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Overdue Balance</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.overdueMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          overdueMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.overdueMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          overdueMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Credit Limit Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Credit Limit</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.creditLimitMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          creditLimitMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.creditLimitMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          creditLimitMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Performance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Payment Performance</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.dsoMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          dsoMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.dsoMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          dsoMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-background border-t">
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setAdvancedFilters({
                    customers: [],
                    currencies: [],
                    paymentTerms: [],
                    collectionStrategies: [],
                    creditControllers: [],
                    accountManagers: [],
                    outstandingMin: "",
                    outstandingMax: "",
                    overdueMin: "",
                    overdueMax: "",
                    creditLimitMin: "",
                    creditLimitMax: "",
                    dsoMin: "",
                    dsoMax: "",
                  });
                }}
              >
                Clear All
              </Button>
              <Button
                className="flex-1"
                onClick={() => setMoreFiltersOpen(false)}
              >
                Apply Filters
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        visibleColumns={visibleColumns}
        onColumnsChange={setVisibleColumns}
      />

      {/* Floating Action Toolbar - Shown when customers are selected */}
      {selectedCustomers.length > 0 && (
        <CustomerSelectionToolbar
          selectedCount={selectedCustomers.length}
          selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
          onClearSelection={() => setSelectedCustomers([])}
          onSendEmail={handleSendEmail}
          onLogCall={handleLogCall}
          onSendStatements={handleExportStatement}
          onAssignStrategy={handleAssignStrategy}
          onCreateCollectionCase={handleCreateCollectionCase}
          onAssignCreditController={handleAssignCreditController}
          onAssignAccountManager={handleAssignAccountManager}
          onExportToExcel={handleExportToExcel}
          onCreatePromise={handleCreatePromise}
          onAddNote={handleAddNote}
          onCreateDispute={handleCreateDispute}
        />
      )}

      {/* Assign Collection Strategy Dialog */}
      <AssignStrategyDialog
        open={assignStrategyDialog}
        onOpenChange={setAssignStrategyDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        strategies={mockStrategies}
        onConfirm={handleConfirmStrategyAssignment}
      />

      {/* Assign Credit Controller Dialog */}
      <AssignCreditControllerDialog
        open={assignCreditControllerDialog}
        onOpenChange={setAssignCreditControllerDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        users={mockUsers}
        onConfirm={handleConfirmCreditControllerAssignment}
      />

      {/* Assign Account Manager Dialog */}
      <AssignAccountManagerDialog
        open={assignAccountManagerDialog}
        onOpenChange={setAssignAccountManagerDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        users={mockUsers}
        onConfirm={handleConfirmAccountManagerAssignment}
      />

      {/* Create Collection Case Drawer */}
      {selectedCustomer_floatingMenu && (
        <CreateCaseDrawer
          open={createCaseDrawerOpen}
          onOpenChange={setCreateCaseDrawerOpen}
          onCaseCreated={() => {
            toast.success("Collection case created successfully");
            setSelectedCustomers([]);
          }}
          prePopulatedCustomer={{
            customerId: selectedCustomer_floatingMenu.id,
            customerName: selectedCustomer_floatingMenu.name,
            accountNumber: selectedCustomer_floatingMenu.accountNumber,
          }}
        />
      )}

      {/* Create Promise to Pay Drawer */}
      {selectedCustomer_floatingMenu && (
        <CreatePromiseDrawer
          open={createPromiseDrawerOpen}
          onOpenChange={setCreatePromiseDrawerOpen}
          onSave={() => {
            toast.success("Promise to pay created successfully");
            setSelectedCustomers([]);
          }}
          prePopulatedCustomer={{
            customerId: selectedCustomer_floatingMenu.id,
            customerName: selectedCustomer_floatingMenu.name,
            accountNumber: selectedCustomer_floatingMenu.accountNumber,
          }}
        />
      )}

      {/* Add Note Modal */}
      {selectedCustomer_floatingMenu && (
        <AddNoteModal
          open={addNoteDialog}
          onOpenChange={setAddNoteDialog}
          customerName={selectedCustomer_floatingMenu.name}
          customerId={selectedCustomer_floatingMenu.id}
          onSave={() => {
            toast.success("Note added successfully");
            setSelectedCustomers([]);
          }}
        />
      )}

      {/* Create Dispute Drawer */}
      <Sheet open={createDisputeDialog} onOpenChange={setCreateDisputeDialog}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          <SheetHeader className="px-0">
            <SheetTitle>Create New Dispute</SheetTitle>
            <SheetDescription>
              Enter dispute details to create a new case
            </SheetDescription>
          </SheetHeader>

          <Tabs defaultValue="basic-info" className="mt-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic-info">Basic Info</TabsTrigger>
              <TabsTrigger value="workflow">Workflow</TabsTrigger>
              <TabsTrigger value="communications">Communications</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
            </TabsList>

            {/* Tab 1: Basic Info */}
            <TabsContent value="basic-info" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="customer">Customer</Label>
                  <Select value={createDisputeForm.customerId} onValueChange={(value) => handleDisputeFormChange("customerId", value)}>
                    <SelectTrigger id="customer">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerOptions.map((customer) => (
                        <SelectItem key={customer.id} value={String(customer.id)}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Dispute Type</Label>
                  <Select value={createDisputeForm.disputeTypeId} onValueChange={(value) => handleDisputeFormChange("disputeTypeId", value)}>
                    <SelectTrigger id="type">
                      <SelectValue placeholder="Select dispute type" />
                    </SelectTrigger>
                    <SelectContent>
                      {disputeTypeOptions.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Disputed Amount</Label>
                  <Input 
                    id="amount" 
                    type="number" 
                    placeholder="0.00"
                    value={createDisputeForm.amount}
                    onChange={(e) => handleDisputeFormChange("amount", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={createDisputeForm.priorityId} onValueChange={(value) => handleDisputeFormChange("priorityId", value)}>
                    <SelectTrigger id="priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Critical</SelectItem>
                      <SelectItem value="2">High</SelectItem>
                      <SelectItem value="3">Medium</SelectItem>
                      <SelectItem value="4">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="assigned-to">Assigned To</Label>
                  <Select value={createDisputeForm.assignedToUserId} onValueChange={(value) => handleDisputeFormChange("assignedToUserId", value)}>
                    <SelectTrigger id="assigned-to">
                      <SelectValue placeholder="Select user (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {userOptions.map((user) => (
                        <SelectItem key={user.id} value={String(user.id)}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="due-date">Due Date</Label>
                  <Input 
                    id="due-date" 
                    type="date"
                    value={createDisputeForm.dueDate}
                    onChange={(e) => handleDisputeFormChange("dueDate", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the dispute in detail..."
                  rows={6}
                  value={createDisputeForm.description}
                  onChange={(e) => handleDisputeFormChange("description", e.target.value)}
                />
              </div>

              <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Bot className="h-5 w-5 text-purple-500 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">AI-Powered Processing</p>
                      <p className="text-xs text-muted-foreground">
                        This dispute will be automatically categorized, prioritized, and routed to the
                        appropriate workflow by the Dispute Resolute Agent.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 2: Workflow */}
            <TabsContent value="workflow" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Select Workflow</CardTitle>
                  <CardDescription>Choose the appropriate workflow for this dispute</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="workflow-select">Workflow Template</Label>
                    <Select defaultValue="standard">
                      <SelectTrigger id="workflow-select">
                        <SelectValue placeholder="Select workflow" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Billing Dispute</SelectItem>
                        <SelectItem value="escalated">Escalated Quality Dispute</SelectItem>
                        <SelectItem value="payment">Standard Payment Dispute</SelectItem>
                        <SelectItem value="quick">Quick Resolution</SelectItem>
                        <SelectItem value="legal">Legal Review Required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-4 mt-6">
                    <p className="text-sm font-medium">Workflow Stages (4 levels)</p>
                    <div className="space-y-6">
                      {[
                        {
                          level: 1,
                          title: "Initial Review & Categorization",
                          description: "Dispute received and automatically categorized by AI",
                        },
                        {
                          level: 2,
                          title: "Investigation & Evidence Gathering",
                          description: "Collecting relevant documents and customer communications",
                        },
                        {
                          level: 3,
                          title: "Resolution Recommendation",
                          description: "AI generates resolution recommendation based on analysis",
                        },
                        {
                          level: 4,
                          title: "Final Approval & Closure",
                          description: "Management approval and customer notification",
                        },
                      ].map((step) => (
                        <div key={step.level} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                              <span className="text-sm">{step.level}</span>
                            </div>
                            {step.level < 4 && (
                              <div className="w-0.5 h-12 bg-muted" />
                            )}
                          </div>
                          <div className="flex-1 pb-8">
                            <h4 className="font-semibold">{step.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 3: Communications */}
            <TabsContent value="communications" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Initial Communication</CardTitle>
                  <CardDescription>Add any initial notes or communications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="initial-note">Initial Note</Label>
                    <Textarea
                      id="initial-note"
                      placeholder="Add any relevant notes about this dispute..."
                      rows={6}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="notify-customer" />
                    <Label htmlFor="notify-customer" className="text-sm font-normal cursor-pointer">
                      Send notification to customer about dispute creation
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="notify-assignee" />
                    <Label htmlFor="notify-assignee" className="text-sm font-normal cursor-pointer">
                      Notify assigned team member
                    </Label>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Communication History</p>
                      <p className="text-xs text-muted-foreground">
                        Communication history will be available after the dispute is created.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 4: Documents */}
            <TabsContent value="documents" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Supporting Documents</CardTitle>
                  <CardDescription>Upload any relevant documents for this dispute</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm font-medium mb-1">Drop files here or click to upload</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 10MB)
                    </p>
                    <Button variant="outline" size="sm">
                      <FileText className="mr-2 h-4 w-4" />
                      Browse Files
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Document Types</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-invoice" />
                        <Label htmlFor="doc-invoice" className="text-sm font-normal cursor-pointer">
                          Invoice
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-contract" />
                        <Label htmlFor="doc-contract" className="text-sm font-normal cursor-pointer">
                          Contract
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-email" />
                        <Label htmlFor="doc-email" className="text-sm font-normal cursor-pointer">
                          Email Correspondence
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-other" />
                        <Label htmlFor="doc-other" className="text-sm font-normal cursor-pointer">
                          Other
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 5: AI Insights */}
            <TabsContent value="ai-insights" className="space-y-4">
              <Card className="border-purple-200 dark:border-purple-800">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    <CardTitle className="text-lg">AI-Powered Analysis</CardTitle>
                  </div>
                  <CardDescription>
                    Preview of AI capabilities for dispute resolution
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Automated Categorization</Label>
                    <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                      <CardContent className="pt-4">
                        <p className="text-sm">
                          The AI agent will automatically analyse the dispute description and categorise it
                          based on historical patterns and similar cases.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-2">
                    <Label>Priority Assessment</Label>
                    <p className="text-sm text-muted-foreground">
                      AI will evaluate the dispute amount, customer history, and urgency to recommend
                      an appropriate priority level.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Similar Cases</Label>
                    <p className="text-sm text-muted-foreground">
                      Once created, the system will analyse similar historical disputes to provide
                      resolution recommendations and estimated timelines.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Workflow Routing</Label>
                    <p className="text-sm text-muted-foreground">
                      The dispute will be automatically routed to the most appropriate workflow based
                      on its characteristics and complexity.
                    </p>
                  </div>

                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">AI Analysis Available After Creation</p>
                          <p className="text-xs text-muted-foreground">
                            Detailed AI insights and recommendations will be generated once the dispute
                            is created and processed by the system.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <SheetFooter className="mt-6 px-0">
            <Button variant="outline" onClick={() => setCreateDisputeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitCreateDispute}
              disabled={createDisputeMutation.isPending || !createDisputeForm.customerId || !createDisputeForm.disputeTypeId || !createDisputeForm.amount || !createDisputeForm.priorityId}
            >
              {createDisputeMutation.isPending ? "Creating..." : "Create Dispute"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
